import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface OpenPosition {
  id: string;
  jobTitle: string;
  contractType: string;
  location: string;
  currentStatus: string; // Open, In Review, Closed
  totalApplications: number;
  applicationFunnel: {
    cvReview: { count: number; pendingTasks: number; overdue: boolean };
    interview: { count: number; pendingTasks: number; overdue: boolean };
    offer: { count: number; pendingTasks: number; overdue: boolean };
  };
}

const mockOpenPositions: OpenPosition[] = [
  {
    id: "1",
    jobTitle: "Marine Ecotourism Specialist",
    contractType: "Full-Time",
    location: "Bahamas",
    currentStatus: "Open",
    totalApplications: 25,
    applicationFunnel: {
      cvReview: { count: 9, pendingTasks: 3, overdue: true },
      interview: { count: 5, pendingTasks: 2, overdue: false },
      offer: { count: 3, pendingTasks: 1, overdue: false },
    },
  },
  {
    id: "2",
    jobTitle: "Sustainable Wildlife Guide",
    contractType: "Part-Time",
    location: "Kenya",
    currentStatus: "In Review",
    totalApplications: 30,
    applicationFunnel: {
      cvReview: { count: 10, pendingTasks: 0, overdue: false },
      interview: { count: 8, pendingTasks: 4, overdue: true },
      offer: { count: 2, pendingTasks: 1, overdue: false },
    },
  },
];

export function OpenPositions() {
  const [positions] = useState<OpenPosition[]>(mockOpenPositions);

  return (
    <div className="space-y-6">
      {positions.map((position) => (
        <Card
          key={position.id}
          className="p-6 shadow-md bg-white hover:shadow-lg transition-shadow rounded-lg"
        >
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">{position.jobTitle}</h3>
              <p className="text-sm text-gray-500">{position.contractType}</p>
              <p className="text-sm text-gray-500">{position.location}</p>
              <Badge
                className={`mt-2 ${
                  position.currentStatus === "Open"
                    ? "bg-green-100 text-green-600"
                    : position.currentStatus === "In Review"
                    ? "bg-yellow-100 text-yellow-600"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {position.currentStatus}
              </Badge>
            </div>
            <Button variant="outline" className="text-sm">
              Manage Applications
            </Button>
          </div>

          {/* Application Funnel */}
          <div className="mt-4">
            <h4 className="font-medium text-gray-700 mb-2">Hiring Stages</h4>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(position.applicationFunnel).map(([stage, data]) => (
                <div key={stage} className="flex flex-col items-start p-3 bg-gray-50 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium capitalize text-gray-800">
                      {stage.replace(/([A-Z])/g, " $1")}
                    </span>
                    <Badge
                      className={`${
                        data.overdue ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"
                      }`}
                    >
                      {data.overdue ? "Overdue" : "On Track"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Candidates: <strong>{data.count}</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Pending Tasks: <strong>{data.pendingTasks}</strong>
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Actions */}
          <div className="mt-4">
            <h4 className="font-medium text-gray-700 mb-2">Pending Actions</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              {position.applicationFunnel.cvReview.pendingTasks > 0 && (
                <li>
                  Review <strong>{position.applicationFunnel.cvReview.pendingTasks}</strong> CV(s)
                  that have been pending for more than 3 days.
                </li>
              )}
              {position.applicationFunnel.interview.pendingTasks > 0 && (
                <li>
                  Schedule <strong>{position.applicationFunnel.interview.pendingTasks}</strong>{" "}
                  interview(s).
                </li>
              )}
              {position.applicationFunnel.offer.pendingTasks > 0 && (
                <li>
                  Send <strong>{position.applicationFunnel.offer.pendingTasks}</strong> final
                  offer(s) to candidates.
                </li>
              )}
            </ul>
          </div>
        </Card>
      ))}
    </div>
  );
}
